/**
 * CreativeStudioSection — AI ad-copy generation + review workflow inside
 * the campaign detail page. Generate (angle matrix) → review draft
 * creatives (critique scores shown) → approve/reject → deploy into a
 * placement's channel ad group, optionally binding an ad_creative
 * experiment variant. Challenger generation feeds observed performance
 * back into the prompt server-side.
 */

import { useState } from 'react';
import type { AdoptCreativeDto, AdoptKeywordIdeaDto, AdoptPlacementDto } from '@/lib/api';
import {
  useAdoptCreativeAction,
  useAdoptCreatives,
  useAdoptGenerations,
  useAdoptPlacements,
  useDeployAdoptCreative,
  useGenerateAdoptChallengers,
  useGenerateAdoptCopy,
  useGenerateAdoptKeywords,
  useKillAdoptCreative,
} from '../hooks/useAdvertisingOptimization';

interface Props {
  holderId: string;
  campaignId: string;
}

const ANGLES = [
  'feature_led',
  'pain_led',
  'social_proof',
  'price_led',
  'urgency',
  'curiosity',
] as const;

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  approved: 'bg-sky-100 text-sky-800',
  deploying: 'bg-amber-100 text-amber-800',
  live: 'bg-emerald-100 text-emerald-800',
  paused: 'bg-amber-100 text-amber-800',
  killed: 'bg-rose-100 text-rose-800',
  rejected: 'bg-rose-100 text-rose-800',
  error: 'bg-rose-100 text-rose-800',
};

function GeneratePanel({ holderId, campaignId }: Props) {
  const generateCopy = useGenerateAdoptCopy(holderId, campaignId);
  const generateChallengers = useGenerateAdoptChallengers(holderId, campaignId);
  const [selectedAngles, setSelectedAngles] = useState<string[]>(['feature_led', 'pain_led']);
  const [instructions, setInstructions] = useState('');

  const toggleAngle = (angle: string): void => {
    setSelectedAngles((prev) =>
      prev.includes(angle) ? prev.filter((a) => a !== angle) : [...prev, angle],
    );
  };

  const busy = generateCopy.isPending || generateChallengers.isPending;
  const lastResult = generateCopy.data ?? generateChallengers.data;

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
      <h3 className="font-medium">Generate ad copy</h3>
      <div className="flex flex-wrap gap-2">
        {ANGLES.map((angle) => (
          <button
            key={angle}
            onClick={() => toggleAngle(angle)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              selectedAngles.includes(angle)
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {angle.replace('_', ' ')}
          </button>
        ))}
      </div>
      <textarea
        rows={2}
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        placeholder="Optional instructions (e.g. lean into the free tier)"
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
      />
      <div className="flex gap-2">
        <button
          onClick={() =>
            generateCopy.mutate({
              angles: selectedAngles,
              instructions: instructions.trim() || undefined,
            })
          }
          disabled={busy || selectedAngles.length === 0}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          {generateCopy.isPending ? 'Generating…' : 'Generate copy'}
        </button>
        <button
          onClick={() => generateChallengers.mutate({})}
          disabled={busy}
          title="Generates new variants informed by the performance of ads already running"
          className="px-3 py-1.5 rounded-md border border-border text-sm disabled:opacity-50"
        >
          {generateChallengers.isPending ? 'Generating…' : 'Generate challengers'}
        </button>
      </div>
      {(generateCopy.isError || generateChallengers.isError) && (
        <div className="text-sm text-rose-600">
          {((generateCopy.error ?? generateChallengers.error) as Error | null)?.message ??
            'Generation failed'}
        </div>
      )}
      {lastResult && (
        <div className="text-sm text-muted-foreground">
          Last run: {lastResult.creatives.length} draft(s) created
          {lastResult.rejectedCount > 0
            ? `, ${lastResult.rejectedCount} candidate(s) rejected by constraint/critique checks`
            : ''}
          .
        </div>
      )}
    </div>
  );
}

function KeywordsPanel({ holderId, campaignId }: Props) {
  const generateKeywords = useGenerateAdoptKeywords(holderId, campaignId);
  const [seed, setSeed] = useState('');
  const keywords: AdoptKeywordIdeaDto[] = generateKeywords.data?.keywords ?? [];

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
      <h3 className="font-medium">Keyword ideas</h3>
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          placeholder="Optional seed terms, comma-separated"
          value={seed}
          onChange={(e) => setSeed(e.target.value)}
        />
        <button
          onClick={() =>
            generateKeywords.mutate({
              seedTerms: seed
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          disabled={generateKeywords.isPending}
          className="px-3 py-1.5 rounded-md border border-border text-sm disabled:opacity-50"
        >
          {generateKeywords.isPending ? 'Thinking…' : 'Suggest keywords'}
        </button>
      </div>
      {generateKeywords.isError && (
        <div className="text-sm text-rose-600">Keyword generation failed — try again.</div>
      )}
      {keywords.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {keywords.map((k, i) => (
            <li
              key={i}
              className={`text-xs rounded px-1.5 py-0.5 border ${
                k.negative
                  ? 'border-rose-200 text-rose-700 line-through'
                  : 'border-border text-foreground'
              }`}
              title={k.negative ? 'negative keyword' : k.matchType}
            >
              {k.text} <span className="text-muted-foreground">({k.matchType})</span>
            </li>
          ))}
        </ul>
      )}
      {keywords.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Copy the useful ones into a placement's keyword list — struck-through terms are suggested
          negatives.
        </p>
      )}
    </div>
  );
}

function DeployDialog({
  creative,
  placements,
  onDeploy,
  onCancel,
  isPending,
  error,
}: {
  creative: AdoptCreativeDto;
  placements: AdoptPlacementDto[];
  onDeploy: (placementId: string) => void;
  onCancel: () => void;
  isPending: boolean;
  error: string | null;
}) {
  const deployable = placements.filter((p) => p.externalAdGroupRef);
  const [placementId, setPlacementId] = useState(
    creative.placementId && deployable.some((p) => p.id === creative.placementId)
      ? creative.placementId
      : (deployable[0]?.id ?? ''),
  );

  if (deployable.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No deployed placement to receive this ad — deploy a placement first.{' '}
        <button onClick={onCancel} className="text-primary hover:underline">
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        className="rounded-md border border-border bg-background px-2 py-1 text-sm"
        value={placementId}
        onChange={(e) => setPlacementId(e.target.value)}
      >
        {deployable.map((p) => (
          <option key={p.id} value={p.id}>
            {p.channel} · {p.id.slice(-6)} ({p.status})
          </option>
        ))}
      </select>
      <button
        onClick={() => onDeploy(placementId)}
        disabled={!placementId || isPending}
        className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs disabled:opacity-50"
      >
        {isPending ? 'Deploying…' : 'Deploy ad'}
      </button>
      <button onClick={onCancel} className="px-2.5 py-1 rounded-md border border-border text-xs">
        Cancel
      </button>
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </div>
  );
}

function CreativeCard({
  creative,
  holderId,
  campaignId,
  placements,
}: {
  creative: AdoptCreativeDto;
  holderId: string;
  campaignId: string;
  placements: AdoptPlacementDto[];
}) {
  const action = useAdoptCreativeAction(holderId, campaignId);
  const deploy = useDeployAdoptCreative(holderId, campaignId);
  const kill = useKillAdoptCreative(holderId, campaignId);
  const [deploying, setDeploying] = useState(false);
  const scores = creative.critique?.scores;

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`text-xs rounded px-1.5 py-0.5 ${STATUS_STYLES[creative.status] ?? 'bg-muted'}`}
          >
            {creative.status}
          </span>
          <span className="text-xs text-muted-foreground">{creative.angle.replace('_', ' ')}</span>
          <span className="text-xs text-muted-foreground">
            {creative.createdBy === 'ai' ? 'AI-generated' : 'manual'}
          </span>
        </div>
        <div className="flex gap-1.5">
          {['draft', 'pending_approval'].includes(creative.status) && (
            <>
              <button
                onClick={() => action.mutate({ creativeId: creative.id, verb: 'approve' })}
                disabled={action.isPending}
                className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => action.mutate({ creativeId: creative.id, verb: 'reject' })}
                disabled={action.isPending}
                className="px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
              >
                Reject
              </button>
            </>
          )}
          {creative.status === 'approved' && !deploying && (
            <button
              onClick={() => setDeploying(true)}
              className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs"
            >
              Deploy…
            </button>
          )}
          {creative.status === 'live' && (
            <button
              onClick={() => action.mutate({ creativeId: creative.id, verb: 'pause' })}
              disabled={action.isPending}
              className="px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
            >
              Pause
            </button>
          )}
          {creative.status === 'paused' && (
            <button
              onClick={() => action.mutate({ creativeId: creative.id, verb: 'resume' })}
              disabled={action.isPending}
              className="px-2.5 py-1 rounded-md bg-emerald-600 text-white text-xs disabled:opacity-50"
            >
              Resume
            </button>
          )}
          {['live', 'paused'].includes(creative.status) && (
            <button
              onClick={() => {
                const reason = window.prompt('Reason for killing this creative?');
                if (reason) kill.mutate({ creativeId: creative.id, reason });
              }}
              disabled={kill.isPending}
              className="px-2.5 py-1 rounded-md border border-rose-300 text-rose-700 text-xs disabled:opacity-50"
            >
              Kill
            </button>
          )}
        </div>
      </div>

      {deploying && (
        <DeployDialog
          creative={creative}
          placements={placements}
          onDeploy={(placementId) =>
            deploy.mutate(
              { creativeId: creative.id, placementId },
              { onSuccess: () => setDeploying(false) },
            )
          }
          onCancel={() => setDeploying(false)}
          isPending={deploy.isPending}
          error={deploy.isError ? ((deploy.error as Error)?.message ?? 'Deploy failed') : null}
        />
      )}

      <div className="text-sm">
        <div className="font-medium">
          {creative.content.headlines.map((h) => h.text).join(' · ')}
        </div>
        <div className="text-muted-foreground mt-1">
          {creative.content.descriptions.map((d) => d.text).join(' ')}
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {scores && (
          <span title="AI critique scores: compliance / distinctiveness / landing match">
            critique {scores.compliance.toFixed(1)} / {scores.distinctiveness.toFixed(1)} /{' '}
            {scores.landingMatch.toFixed(1)}
          </span>
        )}
        {creative.externalAdRef && <span>channel ad {creative.externalAdRef}</span>}
        {creative.killedReason && <span className="text-rose-600">{creative.killedReason}</span>}
      </div>
      {(action.isError || kill.isError) && (
        <div className="text-xs text-rose-600">
          {((action.error ?? kill.error) as Error | null)?.message ?? 'Action failed'}
        </div>
      )}
    </div>
  );
}

export function CreativeStudioSection({ holderId, campaignId }: Props) {
  const creativesQuery = useAdoptCreatives(holderId, campaignId);
  const placementsQuery = useAdoptPlacements(holderId, campaignId);
  const generationsQuery = useAdoptGenerations(holderId, campaignId);
  const [showHistory, setShowHistory] = useState(false);

  const creatives = creativesQuery.data ?? [];

  return (
    <section className="flex flex-col gap-4">
      <GeneratePanel holderId={holderId} campaignId={campaignId} />
      <KeywordsPanel holderId={holderId} campaignId={campaignId} />

      <div className="flex items-center justify-between">
        <h2 className="font-medium">Creatives ({creatives.length})</h2>
        <button
          onClick={() => setShowHistory((s) => !s)}
          className="text-sm text-primary hover:underline"
        >
          {showHistory ? 'Hide generation history' : 'Generation history'}
        </button>
      </div>

      {showHistory && (
        <div className="rounded-lg border border-border p-4 text-sm">
          {(generationsQuery.data ?? []).length === 0 ? (
            <p className="text-muted-foreground">No generations yet.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {(generationsQuery.data ?? []).map((g) => (
                <li key={g.id} className="flex items-center gap-2">
                  <span className="font-medium">{g.kind}</span>
                  <span className="text-muted-foreground">
                    {new Date(g.createdAt).toLocaleString()} · {g.model} ·{' '}
                    {g.acceptedCreativeIds.length} accepted, {g.rejectedCount} rejected
                  </span>
                  {g.error && <span className="text-rose-600 text-xs">{g.error}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {creativesQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading creatives…</p>
      ) : creatives.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No creatives yet — generate a batch above to get started.
        </p>
      ) : (
        creatives.map((c) => (
          <CreativeCard
            key={c.id}
            creative={c}
            holderId={holderId}
            campaignId={campaignId}
            placements={placementsQuery.data ?? []}
          />
        ))
      )}
    </section>
  );
}
